import Highcharts from '../../extensions/Highcharts/js/highchartsInit';
import type {Chart, Point, SVGElement} from 'highcharts';

/** Themes keep their own label ink off labels carrying this class (see www/themes/*.css). */
export const ON_POINT_CLASS = 'data-label-on-point';

const MIN_OPACITY = 0.8;
const GLYPH_HEIGHT_SHARE = 0.75;

/**
 * Highcharts 9 rule for labels it draws on the point (DataLabel.js): inside, negative distance or stacked.
 * A column label justified back into the plot over its own bar gets `inside` set by justifyDataLabel, so it is covered too.
 */
function placedOnPointByOptions(point: Point, label: SVGElement): boolean {
    const options = (label as any).options ?? {};
    const distance = options.distance ?? (point as any).labelDistance;
    return (distance === undefined && options.inside === true) || distance < 0 || !!point.series.options.stacking;
}

function shapeOf(point: Point): SVGGraphicsElement | undefined {
    const shape = (point as any).graphic?.element as SVGGraphicsElement | undefined;
    return shape?.getBBox ? shape : undefined;
}

/** Translucent bubbles and low-base bars show the page through, so contrast against the point colour does not hold. */
function opaque(shape: SVGGraphicsElement): boolean {
    const alpha = Highcharts.color(shape.getAttribute('fill') ?? '').rgba[3];
    const opacity = Number(shape.getAttribute('opacity') ?? 1) * Number(shape.getAttribute('fill-opacity') ?? 1);
    // A gradient or pattern fill does not parse to a colour, so it is not treated as opaque
    return Number.isFinite(alpha) && alpha * opacity >= MIN_OPACITY;
}

interface Fit {
    /** Along the stack, where an overflowing label lands on the neighbouring segments */
    along: boolean;
    /** Across the stack, where it only spills into the gap between bars */
    across: boolean;
}

function fit(point: Point, shape: SVGGraphicsElement, label: SVGElement): Fit {
    const inverted = point.series.chart.inverted;
    // Series groups of an inverted chart are rotated, so the shape's own box has the axes swapped
    const {width, height} = shape.getBBox();
    const [shapeWidth, shapeHeight] = inverted ? [height, width] : [width, height];
    // The text, not the label box: its padding may freely overlap the neighbours
    const text = ((label as any).text ?? label).getBBox();
    // Digits and "%" only reach the cap height, the line box above and below them is empty
    const textHeight = text.height * GLYPH_HEIGHT_SHARE;
    return inverted
        ? {along: text.width <= shapeWidth, across: textHeight <= shapeHeight}
        : {along: textHeight <= shapeHeight, across: text.width <= shapeWidth};
}

/**
 * A stacked label longer than its segment spills over the neighbouring colours, where no ink is readable,
 * so it is dropped (the value stays in the tooltip). Mirrors how Highcharts 9 drops a disabled label (DataLabel.js),
 * so the next redraw draws it afresh and it comes back once the segment is wide enough.
 */
function drop(point: any, label: SVGElement, index: number): void {
    label.destroy();
    if (point.dataLabels?.length === 1) {
        delete point.dataLabels;
    } else if (point.dataLabels) {
        delete point.dataLabels[index];
    }
    if (index === 0) {
        delete point.dataLabel;
    }
}

function relativeLuminance(color: string): number {
    const [r, g, b] = Highcharts.color(color).rgba.slice(0, 3).map((channel: number) => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Black or white, whichever has the higher WCAG contrast ratio against the point.
 * renderer.getContrast weights channels ad hoc and puts white on mid tones such as #ff7f0e (2.5:1) where black reaches 8.3:1.
 */
function contrastInk(color: string): string {
    const luminance = relativeLuminance(color);
    return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? '#000000' : '#FFFFFF';
}

function paint(chart: Chart): void {
    for (const series of chart.series) {
        for (const point of series.points ?? []) {
            const labels: SVGElement[] = (point as any).dataLabels ?? (point.dataLabel ? [point.dataLabel] : []);
            labels.forEach((label, index) => {
                if (!label?.element) {
                    return;
                }
                const shape = placedOnPointByOptions(point, label) ? shapeOf(point) : undefined;
                const labelFit = shape ? fit(point, shape, label) : undefined;
                const stackedBar = !!series.options.stacking && (point as any).shapeType === 'rect';
                if (labelFit && !labelFit.along && stackedBar) {
                    drop(point, label, index);
                // A stacked label spilling across a thin bar only reaches the gap, a halo there would glow over the segments
                } else if (labelFit?.along && (labelFit.across || stackedBar) && typeof point.color === 'string' && opaque(shape!)) {
                    // Ink against the point, not the page: a theme ink or a halo on a coloured segment is unreadable
                    label.addClass(ON_POINT_CLASS).css({color: contrastInk(point.color), textOutline: 'none'});
                    // Highcharts 9 css({textOutline: 'none'}) only skips drawing a new halo, the one from the option styles stays
                    label.element.querySelectorAll('.highcharts-text-outline').forEach((outline) => outline.remove());
                } else {
                    // Highcharts re-applies the option styles on every redraw, only the class is ours to undo
                    label.removeClass(ON_POINT_CLASS);
                }
            });
        }
    }
}

/**
 * Chart callback: runs before the first render event, so every render (incl. resize, legend toggle) is covered.
 * Ordered ahead of the overlap check (OverlappingDataLabels.js, also on render), so a dropped label cannot hide a neighbour.
 */
export function watchDataLabelInk(chart: Chart): void {
    Highcharts.addEvent(chart, 'render', () => paint(chart), {order: 0});
}
